import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext, useSearchParams, useNavigate } from 'react-router-dom';
import { Download, Lock } from 'lucide-react';
import styles from './ChatPage.module.css';
import GenerateReportButton from '../reports/GenerateReportButton';
import { ChatHeader } from './components/ChatHeader';
import { ChatBody } from './components/ChatBody';
import { ChatFooter } from './components/ChatFooter';
import { ChatSource, Message } from './schemas';
import { useAuth } from '../../context/AuthContext';
import { useBilling } from '../../context/BillingContext';
import { websocketService } from '../../websocket';

interface OutletContext {
  selectedSessionId: number | null;
  setSelectedSessionId: (id: number | null) => void;
  refreshSessions: () => void;
}

interface ChatMessage {
  id: number;
  role: string;
  content: string;
  sources: string | null;
  feedback?: 'up' | 'down' | null;
  created_at: string;
}

/**
 * Optional caller-supplied control for the chat session state.
 * When ChatPage is rendered as a child of <Outlet> (e.g. inside AppLayout),
 * the outletContext provides this. When it's embedded inside ChatShell on
 * a route that ISN'T wrapped by AppLayout (e.g. /admin/dashboard/chat),
 * the parent must pass `sessionsControl` explicitly.
 */
export interface ChatPageProps {
  sessionsControl?: OutletContext;
}

export interface Attachment {
  name: string;
  text: string;
  truncated?: boolean;
}

export const ChatPage: React.FC<ChatPageProps> = ({ sessionsControl }) => {
  const outletContext = useOutletContext<OutletContext | null>();
  const ctl: OutletContext | null = sessionsControl ?? outletContext ?? null;
  const selectedSessionId = ctl?.selectedSessionId ?? null;
  const setSelectedSessionId: OutletContext['setSelectedSessionId'] =
    ctl?.setSelectedSessionId ?? (() => undefined);
  const refreshSessions: OutletContext['refreshSessions'] =
    ctl?.refreshSessions ?? (() => undefined);
  const navigate = useNavigate();
  const { canUse } = useBilling();
  // PDF export is a Professional+ feature. Admins have no subscription (canUse
  // would be false for them) so exempt them explicitly; trial/pro users pass.
  const isAdmin = (() => {
    try { return !!JSON.parse(localStorage.getItem('auth_meta') || '{}').is_admin; } catch { return false; }
  })();
  const canExport = isAdmin || canUse('export_reporting');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isThinking, setIsThinking] = useState<boolean>(false);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [retrievalUrls, setRetrievalUrls] = useState<ChatSource[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState<boolean>(false);
  const [, setAbortController] = useState<AbortController | null>(null);
  const [suggestionText, setSuggestionText] = useState<string | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasActiveConnectionRef = useRef(false);
  const streamingMessageIdRef = useRef<number | null>(null);
  // Holds the answer the WEBSOCKET already streamed to the user. The companion
  // POST /user_chat is what actually persists the message, but CloudFront caps
  // the API origin at a 60s read timeout — so a long answer streams in fine,
  // then the POST 504s and the old error path REPLACED the visible answer with
  // 'Something went wrong'. That is the 'answer appeared then disappeared' bug.
  // Keep the streamed text so a transport failure cannot destroy a good answer.
  const streamedAnswerRef = useRef<string>('');
  // Set once the websocket 'completed' event has appended the final message.
  // The backend persists the assistant reply BEFORE emitting that event, so at
  // that point the turn is fully done and saved — the POST response is then
  // redundant and its failure (CloudFront 60s) must be ignored entirely.
  const wsFinalizedRef = useRef<boolean>(false);
  const { token, user } = useAuth();
  // For admin sessions, user is null — fall back to the email stored in auth_meta
  const adminEmail = (() => {
    try {
      const raw = window.localStorage.getItem('auth_meta');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.is_admin ? (parsed?.email ?? parsed?.username ?? null) : null;
    } catch { return null; }
  })();
  const userEmail = user?.email ?? adminEmail;

  // Pre-fill the composer from a ?q= param (e.g. opened from a Trending Topic card),
  // then strip the param so it doesn't re-fire on refresh.
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setSuggestionText(q);
      const next = new URLSearchParams(searchParams);
      next.delete('q');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const api_url = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:8000"

  const authHeaders = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  // Attached document for analysis (QA #3): uploaded → text extracted server-side,
  // then passed with the next message and injected into the agent's context.
  // A list, not one: comparing two documents in a single turn was impossible
  // while the composer held a single attachment (SRS #44).
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  const uploadAttachment = async (files: File[]) => {
    if (!files.length) return;
    setAttachError(null);
    setAttaching(true);
    try {
      const form = new FormData();
      // One request for the whole selection, so the plan's per-file checks and
      // the daily count are applied together and a refusal costs nothing.
      files.forEach((f) => form.append('files', f));
      const res = await fetch(`${api_url}/api/v1/chat/user_chat/attachment`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},  // no Content-Type: browser sets multipart boundary
        body: form,
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(typeof b?.detail === 'string' ? b.detail : typeof b?.detail?.message === 'string' ? b.detail.message : 'Could not read that file.');
      }
      const d = await res.json();
      const added: Attachment[] = (d.attachments ?? [{ filename: d.filename, text: d.text, truncated: d.truncated }])
        .map((a: any) => ({ name: a.filename, text: a.text, truncated: a.truncated }));
      setAttachments((prev) => [...prev, ...added]);
    } catch (e: any) {
      setAttachError(e?.message || 'Attachment failed.');
    } finally {
      setAttaching(false);
    }
  };

  /** Each document labelled, so a comparison can attribute what it found. */
  const attachmentPayload = (items: Attachment[]) => {
    if (!items.length) return { text: undefined as string | undefined, name: undefined as string | undefined };
    if (items.length === 1) return { text: items[0].text, name: items[0].name };
    return {
      text: items.map((a, i) => `--- Document ${i + 1}: ${a.name} ---\n${a.text}`).join('\n\n'),
      name: items.map((a) => a.name).join(', '),
    };
  };

  // Fetch chat messages for a session
  const fetchChatMessages = async (sessionId: number) => {
    try {
      const response = await fetch(`${api_url}/api/v1/chat/chats?session_id=${sessionId}`, {
        method: "GET",
        headers: authHeaders,
      });

      if (response.ok) {
        const data = await response.json();
        
        // Convert backend messages to frontend Message format
        const loadedMessages: Message[] = data.messages.map((msg: ChatMessage, index: number) => ({
          id: index + 1,
          sender: msg.role === 'user' ? 'user' : 'bot',
          text: msg.content,
          sources: msg.sources ? JSON.parse(msg.sources) : undefined,
          dbId: msg.id,
          feedback: msg.feedback ?? null,
        }));
        
        setMessages(loadedMessages);
        setCurrentSessionId(sessionId);
      }
    } catch (error) {
      // Error handling without console.error
    }
  };

  // Load messages when a session is selected or when user becomes available.
  // userEmail is included so that if the session is pre-selected but auth
  // wasn't ready yet, the effect re-runs once the user is resolved.
  useEffect(() => {
    if (selectedSessionId) {
      fetchChatMessages(selectedSessionId);
    } else {
      // Clear messages for new chat
      setMessages([]);
      setCurrentSessionId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessionId, userEmail]);

  // Open a specific chat from history via ?session=<id>. Loads it directly so
  // it works even where there is no shared Outlet/sessionsControl context
  // (e.g. the admin dashboard renders Chat without it) — that gap is why
  // "Open" from admin chat history showed no previous messages. Strip the
  // param after load so a refresh doesn't re-trigger it.
  useEffect(() => {
    const s = searchParams.get('session');
    if (!s) return;
    const sid = Number(s);
    if (!Number.isNaN(sid)) {
      setSelectedSessionId(sid);   // reflect into context where it exists
      fetchChatMessages(sid);      // load directly — context-independent
    }
    const next = new URLSearchParams(searchParams);
    next.delete('session');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!token || !userEmail || hasActiveConnectionRef.current) {
      return;
    }

    hasActiveConnectionRef.current = true;
    websocketService.connect(token);

    // Subscribe to WebSocket messages
    const unsubscribe = websocketService.subscribe((data) => {
        // Handle different message types
        if (data.type === 'chat_status') {
          if (data.status === 'started') {
            setIsThinking(true);
            setIsStreaming(false);
            setStreamingContent('');
            setProcessingStatus('Processing your query...');
            setRetrievalUrls([]);
          } else if (data.status === 'processing') {
            // Update processing status message
            setProcessingStatus(data.message || 'Processing...');
          } else if (data.status === 'completed') {
            setIsThinking(false);
            setIsStreaming(false);
            setStreamingContent('');
            setProcessingStatus('');
            // Update retrieval URLs if provided in completed message
            if (data.retrieval_urls && Array.isArray(data.retrieval_urls)) {
              setRetrievalUrls(Array.isArray(data.retrieval_sources) && data.retrieval_sources.length > 0 ? data.retrieval_sources : data.retrieval_urls);
            }
            // Finalise the turn straight from the websocket. The backend has
            // already PERSISTED this reply before emitting 'completed', so the
            // turn is complete and saved at this point — waiting on the POST
            // only exposed us to CloudFront's 60s origin timeout, which erased
            // long answers the user could already see.
            if (typeof data.response === 'string' && data.response.trim()) {
              wsFinalizedRef.current = true;
              // The POST may already have failed (CloudFront cuts it at 60s) and
              // pushed a transport-error message. The real answer has now arrived
              // over the websocket, so retract that error instead of leaving it
              // sitting above a perfectly good reply.
              setMessages((prev) => [
                ...prev.filter((m) => !m.transportError),
                {
                  id: prev.length + 1,
                  sender: 'bot' as const,
                  text: data.response,
                  sources: Array.isArray(data.retrieval_sources) && data.retrieval_sources.length > 0
                    ? data.retrieval_sources
                    : Array.isArray(data.retrieval_urls) && data.retrieval_urls.length > 0
                      ? data.retrieval_urls
                      : undefined,
                  dbId: typeof data.message_id === 'number' ? data.message_id : undefined,
                  feedback: null,
                },
              ]);
              if (data.session_id) {
                setCurrentSessionId((cur) => cur ?? data.session_id);
                setSelectedSessionId(data.session_id);
                refreshSessions();
              }
            }
          } else if (data.status === 'error') {
            setIsThinking(false);
            setIsStreaming(false);
            setStreamingContent('');
            setProcessingStatus('');
          }
        } else if (data.type === 'stream_chunk') {
          // Handle streaming content chunks
          setIsThinking(false); // Stop showing "Thinking..." when streaming starts
          setIsStreaming(true);
          setProcessingStatus('');
          setStreamingContent(data.full_content || '');
          streamedAnswerRef.current = data.full_content || streamedAnswerRef.current;
        } else if (data.type === 'retrieval') {
          setRetrievalUrls(data.data?.sources || data.data?.urls || []);
        }
      });

    return () => {
      hasActiveConnectionRef.current = false;
      unsubscribe();
      websocketService.disconnect();
    };
  }, [token, userEmail]);

  // Broadcast streaming/thinking state so ChatShell (sidebar) can block
  // session switches while a response is mid-stream. Window event keeps
  // ChatPage decoupled from any specific parent shell.
  useEffect(() => {
    const busy = isStreaming || isThinking;
    window.dispatchEvent(
      new CustomEvent('chat:streaming', { detail: { streaming: busy } })
    );
  }, [isStreaming, isThinking]);

  const fetchResponse = async (userMessage: string) => {
    const newMessageId = messages.length + 1;
    setMessages((prevMessages) => [
      ...prevMessages,
      { id: newMessageId, sender: 'user', text: userMessage, attachmentName: attachmentPayload(attachments).name },
    ]);
    
    setIsThinking(true);
    setIsStreaming(false);
    setStreamingContent('');
    setProcessingStatus('Processing your query...');
    setRetrievalUrls([]);
    streamedAnswerRef.current = '';
    wsFinalizedRef.current = false;
    streamingMessageIdRef.current = newMessageId + 1; // Bot message will be next
    
    // Create new abort controller for this request
    const controller = new AbortController();
    setAbortController(controller);
    abortControllerRef.current = controller;

    try {
      const response = await fetch(`${api_url}/api/v1/chat/user_chat`, {
        method : "POST",
        headers: authHeaders,
        body: JSON.stringify({
          user_query : userMessage,
          thread_id : "123123123",
          session_id: currentSessionId,
          attachment_text: attachmentPayload(attachments).text,
          attachment_name: attachmentPayload(attachments).name,
        }),
        signal: controller.signal
      })
      // One-shot: the attached doc applies to this turn only.
      if (attachments.length) { setAttachments([]); setAttachError(null); }

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // detail may be a string or an object {message, session_id} from the backend
        const detail = data?.detail;
        const rawDetail = typeof detail === 'string' ? detail
          : typeof detail?.message === 'string' ? detail.message
          : Array.isArray(detail) ? detail.map((d: { msg?: string }) => d?.msg).filter(Boolean).join('; ')
          : '';
        const errorSessionId: number | null = typeof detail === 'object' && detail?.session_id ? detail.session_id : null;

        const errorMessage = (() => {
          const s = response.status;
          const d = rawDetail.toLowerCase();
          // Plan limits speak for themselves: the server's message says what was used and what lifts it.
          if (detail?.code === 'quota_exceeded' || detail?.code === 'feature_locked') return rawDetail;
          if (s === 429 || d.includes('quota') || d.includes('insufficient_quota') || d.includes('rate limit'))
            return "I'm currently unavailable due to high demand. Please try again in a few moments.";
          if (s === 401 || s === 403)
            return "You don't have permission to use this feature. Please log in again.";
          if (s === 404)
            return "Your account wasn't found. Please log out and log back in.";
          if (d.includes('timeout') || d.includes('timed out'))
            return "The request took too long. Please try a shorter question.";
          if (s >= 500)
            return "Something went wrong on our end. Please try again shortly.";
          return "Something went wrong. Please try again.";
        })();

        // The websocket may already have streamed a COMPLETE answer before the
        // POST failed (CloudFront cuts the API origin at 60s, so long answers
        // reliably 504 even though generation succeeded). Never throw away a
        // good answer for a transport error — keep it and note it is unsaved.
        const streamed = streamedAnswerRef.current.trim();
        // Websocket already delivered AND persisted this answer — the POST
        // failing afterwards is cosmetic, so add nothing. NB: only the message
        // push is skipped; the state cleanup below must still run or the
        // spinner and abort controller would be left dangling.
        if (!wsFinalizedRef.current) setMessages((prevMessages) => [
          ...prevMessages,
          streamed
            ? {
                id: prevMessages.length + 1,
                sender: 'bot' as const,
                transportError: true,
                text: streamed +
                  '\n\n_(This answer took longer than the 60s gateway limit, so it could not be ' +
                  'saved to your chat history. Copy it if you need it.)_',
                sources: retrievalUrls.length > 0 ? retrievalUrls : undefined,
              }
            : { id: prevMessages.length + 1, sender: 'bot' as const, text: errorMessage, transportError: true },
        ]);

        // Link the UI to the session that was created so clicking it shows this error
        if (errorSessionId && !currentSessionId) {
          setCurrentSessionId(errorSessionId);
          setSelectedSessionId(errorSessionId);
          refreshSessions();
        }

        setIsThinking(false);
        setIsStreaming(false);
        setStreamingContent('');
        setProcessingStatus('');
        setRetrievalUrls([]);
        setAbortController(null);
        abortControllerRef.current = null;
        return;
      }

      const llm_response = data.query_response ?? data.final_answer ?? 'No response from the server.';
      const newSessionId = data.session_id;
      const urls: ChatSource[] = Array.isArray(data.retrieval_sources) && data.retrieval_sources.length > 0
        ? data.retrieval_sources
        : Array.isArray(data.retrieval_urls) ? data.retrieval_urls : retrievalUrls;

      // Add the final bot message to the messages array. Attach the DB message
      // id (when the backend returns it) so feedback thumbs render immediately
      // on this fresh reply — incl. fresh/admin chats, not just reloaded ones.
      // The websocket may already have appended this exact reply (it carries the
      // text, sources and message_id). Appending again would duplicate the turn.
      if (!wsFinalizedRef.current) setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: prevMessages.length + 1,
          sender: 'bot',
          text: llm_response,
          sources: urls.length > 0 ? urls : undefined,
          dbId: typeof data.message_id === 'number' ? data.message_id : undefined,
          feedback: null,
        },
      ]);

      if (!currentSessionId && newSessionId) {
        setCurrentSessionId(newSessionId);
        setSelectedSessionId(newSessionId);
        refreshSessions();
      }
      // Request server-side title generation to replace first-message title
      try {
        const sessionToGenerate = newSessionId || currentSessionId;
        if (sessionToGenerate && userEmail) {
          const genResp = await fetch(`${api_url}/api/v1/chat/sessions/${sessionToGenerate}/generate_title`, {
            method: 'POST',
            headers: authHeaders
          });
          if (genResp.ok) {
            // refresh sessions to show updated title
            refreshSessions();
          }
        }
      } catch (err) {
        // ignore title-generation failures
      }
      setRetrievalUrls(urls);
      setIsThinking(false);
      setIsStreaming(false);
      setStreamingContent('');
      setProcessingStatus('');
      setAbortController(null);
      abortControllerRef.current = null;
      streamingMessageIdRef.current = null;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        setMessages((prevMessages) => prevMessages.slice(0, -1));
      } else {
        // Same reasoning as the !response.ok path: if the websocket already
        // delivered a complete answer, a dropped connection must not erase it.
        const streamedOnDrop = streamedAnswerRef.current.trim();
        // Same as above: skip only the push, never the cleanup.
        if (!wsFinalizedRef.current) setMessages((prevMessages) => [
          ...prevMessages,
          streamedOnDrop
            ? {
                id: prevMessages.length + 1,
                sender: 'bot' as const,
                transportError: true,
                text: streamedOnDrop +
                  '\n\n_(The connection dropped before this could be saved to your chat history. ' +
                  'Copy it if you need it.)_',
              }
            : {
                id: prevMessages.length + 1,
                sender: 'bot' as const,
                transportError: true,
                text: 'Unable to reach the chat service. Please check your connection and try again.',
              },
        ]);
      }
      setIsThinking(false);
      setIsStreaming(false);
      setStreamingContent('');
      setProcessingStatus('');
      setRetrievalUrls([]);
      setAbortController(null);
      abortControllerRef.current = null;
      streamingMessageIdRef.current = null;
    }
  }

  const handleDownloadHtml = async () => {
    if (!canExport) { navigate('/billing/checkout?plan=professional'); return; }
    if (!currentSessionId || !token || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const res = await fetch(`${api_url}/api/v1/chat/sessions/${currentSessionId}/export-html`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTML export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'policyora-chat.html';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* non-fatal */ } finally { setDownloadingPdf(false); }
  };

  const handleDownloadPdf = async () => {
    // Gate: PDF export & reporting is a Professional+ entitlement.
    if (!canExport) { navigate('/billing/checkout?plan=professional'); return; }
    if (!currentSessionId || !token || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const res = await fetch(`${api_url}/api/v1/chat/sessions/${currentSessionId}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`PDF download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'policyora-chat.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      /* download failure is non-fatal; user can retry */
    } finally {
      setDownloadingPdf(false);
    }
  };

  const cancelGeneration = () => {
    const controller = abortControllerRef.current;
    if (!controller) {
      setIsThinking(false);
      setIsStreaming(false);
      setStreamingContent('');
      setProcessingStatus('');
      setRetrievalUrls([]);
      return;
    }

    controller.abort?.();
    setAbortController(null);
    abortControllerRef.current = null;
    setIsThinking(false);
    setIsStreaming(false);
    setStreamingContent('');
    setProcessingStatus('');
    setRetrievalUrls([]);
    streamingMessageIdRef.current = null;
  }

  // Rate an assistant reply (thumbs up/down). Clicking the active rating again
  // clears it. Optimistic update; reverts on error.
  const handleFeedback = async (msg: Message, value: 'up' | 'down') => {
    if (!msg.dbId) return; // only persisted assistant messages are rateable
    const next = msg.feedback === value ? null : value;
    const prev = msg.feedback ?? null;
    setMessages(ms => ms.map(m => (m.id === msg.id ? { ...m, feedback: next } : m)));
    // A thumbs-down is worth a sentence (SRS #106): the admin sees the
    // question, the reply and this reason together. Cancelling the prompt
    // still records the vote.
    const comment = next === 'down' ? (window.prompt('Sorry about that — what was wrong with this answer? (optional)') || undefined) : undefined;
    try {
      const resp = await fetch(`${api_url}/api/v1/chat/messages/${msg.dbId}/feedback`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback: next, comment }),
      });
      if (!resp.ok) throw new Error('feedback failed');
    } catch {
      setMessages(ms => ms.map(m => (m.id === msg.id ? { ...m, feedback: prev } : m)));
    }
  };

  return (
    <div className={styles.chatPage}>
      {currentSessionId && messages.length > 0 && !isThinking && !isStreaming && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <GenerateReportButton sessionId={currentSessionId} className={styles.pdfButton} disabled={downloadingPdf}
                                canUse={canExport} onUpgrade={() => navigate('/billing/checkout?plan=professional')} />
          {canExport && (
            <button
              type="button"
              className={styles.pdfButton}
              onClick={handleDownloadHtml}
              disabled={downloadingPdf}
              title="Download this conversation as a print-ready HTML report"
              aria-label="Download this conversation as HTML"
            >
              <Download size={16} /> HTML
            </button>
          )}
          <button
            type="button"
            className={styles.pdfButton}
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            title={canExport ? 'Download this conversation as PDF' : 'PDF export is a Professional feature — upgrade to unlock'}
            aria-label="Download this conversation as PDF"
          >
            {canExport ? <Download size={16} /> : <Lock size={16} />}
            {downloadingPdf ? 'Preparing…' : canExport ? 'Download PDF' : 'Download PDF (Pro)'}
          </button>
        </div>
      )}
      {messages.length === 0 && !isThinking && !isStreaming
        /* Suggestion cards SEND immediately — clicking "Comparison" etc. must
           produce a visible action in the chat, not just prefill the composer
           (testers read the silent prefill as "tabs don't work"). */
        ? <ChatHeader onSuggestion={(text) => fetchResponse(text)} />
        : <ChatBody
            messages={messages}
            isThinking={isThinking}
            isStreaming={isStreaming}
            streamingContent={streamingContent}
            processingStatus={processingStatus}
            retrievalUrls={retrievalUrls}
            onFeedback={handleFeedback}
          />
      }
      <ChatFooter
        fetchResponse={fetchResponse}
        isGenerating={isThinking || isStreaming}
        onCancelGeneration={cancelGeneration}
        prefillText={suggestionText}
        attachments={attachments.map((a) => ({ name: a.name, truncated: a.truncated }))}
        attaching={attaching}
        attachError={attachError}
        token={token}
        onAttach={uploadAttachment}
        onClearAttachment={(index?: number) => {
          setAttachments((prev) => (index === undefined ? [] : prev.filter((_, i) => i !== index)));
          setAttachError(null);
        }}
      />
    </div>
  );
};
